/**
 * Home Page Component
 *
 * Simplified home page with concept explanation interface.
 * Features concept title input, explanation textarea, and AI explanation button.
 * Includes collapsible explanation tips at the bottom.
 */
import type { Route } from "./+types/home";

import {
  ArrowRight,
  Brain,
  ChevronDown,
  ChevronRight,
  Lightbulb,
  PenTool,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Link, useLoaderData, useNavigation } from "react-router";

import { GuideVideoModal } from "~/core/components/guide-video-modal";
import { LoadingOverlay } from "~/core/components/loading-overlay";
import { PremiumBadge, UpgradePrompt } from "~/core/components/premium-badge";
import { Button } from "~/core/components/ui/button";
import { trackButtonClick } from "~/core/lib/analytics-events.client";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "~/core/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "~/core/components/ui/collapsible";
import { Input } from "~/core/components/ui/input";
import { Label } from "~/core/components/ui/label";
import { Textarea } from "~/core/components/ui/textarea";
import i18next from "~/core/lib/i18next.server";
import makeServerClient, { getSafeUser } from "~/core/lib/supa-client.server";

import { getTodaySessionUsage, isPaidUser } from "../../ai/queries";

/**
 * Meta function for setting page metadata
 *
 * This function generates SEO-friendly metadata for the home page using data from the loader.
 * It sets:
 * - Page title from translated "home.title" key
 * - Meta description from translated "home.subtitle" key
 *
 * The metadata is language-specific based on the user's locale preference.
 *
 * @param data - Data returned from the loader function containing translated title and subtitle
 * @returns Array of metadata objects for the page
 */
export const meta: Route.MetaFunction = ({ data }) => {
  return [
    { title: data?.title },
    { name: "description", content: data?.subtitle },
  ];
};

/**
 * Loader function for server-side data fetching
 *
 * This function is executed on the server before rendering the component.
 * It:
 * 1. Extracts the user's locale from the request (via cookies or Accept-Language header)
 * 2. Creates a translation function for that specific locale
 * 3. Checks if user is authenticated
 * 4. Returns translated strings and user authentication status
 *
 * This approach ensures that even on first load, users see content in their preferred language,
 * which improves both user experience and SEO (search engines see localized content).
 *
 * @param request - The incoming HTTP request containing locale information
 * @returns Object with translated title, subtitle, and user authentication status
 */
export async function loader({ request }: Route.LoaderArgs) {
  // Get a translation function for the user's locale from the request
  const t = await i18next.getFixedT(request);

  // Check if user is authenticated
  const [supabase] = makeServerClient(request);
  const { user } = await getSafeUser(supabase);

  // Get user payment status and usage data if authenticated
  let userIsPaid = false;
  let todayUsage: any[] = [];

  if (user) {
    try {
      [todayUsage, userIsPaid] = await Promise.all([
        getTodaySessionUsage(supabase, user.id),
        isPaidUser(supabase, user.id),
      ]);
    } catch (error) {
      console.warn("Failed to load user payment status:", error);
    }
  }

  // Return translated strings and authentication status for use in both the component and meta function
  return {
    title: t("home.title"),
    subtitle: t("home.subtitle"),
    user,
    isAuthenticated: !!user,
    isPaidUser: userIsPaid,
    usedSessions: todayUsage.length,
  };
}

/**
 * Home page component - Shows different content based on authentication status
 *
 * For unauthenticated users: Beautiful landing page with CTA to login/explain
 * For authenticated users: Direct concept explanation interface
 *
 * Features:
 * - Landing page for visitors with inspiring content and call-to-action
 * - Concept explanation interface for logged-in users
 * - Collapsible explanation tips for authenticated users
 * - Responsive design with dark mode support
 *
 * @returns JSX element representing either landing page or explanation interface
 */
export default function Home() {
  // Get the translation function for the current locale
  const { t } = useTranslation();
  const loaderData = useLoaderData<typeof loader>();
  const navigation = useNavigation();
  const [concept, setConcept] = useState("");
  const [explanation, setExplanation] = useState("");
  const [isTipsOpen, setIsTipsOpen] = useState(false);

  // 가이드 영상 모달 상태
  const [showGuideVideo, setShowGuideVideo] = useState(false);

  const isSubmitting = navigation.state === "submitting";
  const isAuthenticated = loaderData.isAuthenticated;

  // 가이드 영상 표시 로직
  useEffect(() => {
    if (isAuthenticated) {
      // 오늘 날짜로 체크했는지 확인
      const today = new Date().toDateString();
      const dontShowToday = localStorage.getItem("guide-video-dont-show-today");

      if (dontShowToday !== today) {
        // 오늘 아직 체크하지 않았으면 가이드 영상 표시
        setShowGuideVideo(true);
      }
    }
  }, [isAuthenticated]);

  // 가이드 영상 모달 핸들러들
  const handleCloseGuideVideo = () => {
    setShowGuideVideo(false);
  };

  const handleDontShowToday = (checked: boolean) => {
    if (checked) {
      // 오늘 날짜로 저장
      const today = new Date().toDateString();
      localStorage.setItem("guide-video-dont-show-today", today);
    }
  };

  // Show landing page for unauthenticated users
  if (!isAuthenticated) {
    return (
      <div
        className="relative min-h-screen"
        style={{
          width: "100vw",
          marginLeft: "calc(-50vw + 50%)",
          marginTop: "-8rem", // nav bar (4rem) + layout margin (4rem) = 8rem
          // 풋터와 내용이 겹치지 않도록 음수 마진 대신 충분한 패딩을 사용
          paddingBottom: "8rem",
          background:
            "linear-gradient(160deg, rgb(253 243 220 / 0.9) 0%, rgb(250 250 247 / 0.95) 60%, rgb(245 244 239 / 1) 100%)",
        }}
      >
        {/* Background overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background/30 dark:to-background/60"></div>

        <div className="relative z-10">
          {/* Hero Section */}
          <section className="relative px-6 pt-32 pb-16 text-center">
            <div className="mx-auto max-w-4xl">
              <blockquote className="text-foreground font-serif text-2xl leading-relaxed italic md:text-3xl lg:text-4xl">
                "{t("landing.hero.quote")}"
              </blockquote>
              <cite className="text-muted-foreground mt-6 block font-serif text-lg">
                — {t("landing.hero.author")}
              </cite>
            </div>
          </section>

          {/* Introduction Section */}
          <section className="px-6 py-16">
            <div className="mx-auto max-w-4xl text-center">
              <h1 className="text-foreground mb-6 text-4xl font-bold md:text-5xl">
                {t("landing.intro.title")}
              </h1>
              <p className="text-muted-foreground mb-12 text-xl leading-relaxed md:text-2xl">
                {t("landing.intro.subtitle")}
              </p>

              <div className="mb-12 flex flex-col items-center justify-center gap-8 md:flex-row">
                <div className="flex items-center gap-3">
                  <Lightbulb className="h-8 w-8 text-amber-500 dark:text-amber-400" />
                  <span className="text-foreground text-lg">
                    {t("landing.intro.features.organize")}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <PenTool className="h-8 w-8 text-primary" />
                  <span className="text-foreground text-lg">
                    {t("landing.intro.features.practice")}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Brain className="h-8 w-8 text-muted-foreground" />
                  <span className="text-foreground text-lg">
                    {t("landing.intro.features.learning")}
                  </span>
                </div>
              </div>

              {/* CTA Button - Links to Explain (with login redirect if needed) */}
              <Button
                size="lg"
                className="transform rounded-xl bg-primary px-8 py-4 text-lg text-primary-foreground shadow-md transition-all duration-200 hover:scale-[1.02] hover:brightness-95 hover:shadow-lg"
                asChild
              >
                <Link
                  to="/explain"
                  viewTransition
                  onClick={() => trackButtonClick("start_teaching_cta", "landing_hero")}
                >
                  {t("landing.intro.cta")}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </section>

          {/* Examples Section */}
          <section className="bg-white/30 px-6 py-16 backdrop-blur-sm dark:bg-black/20">
            <div className="mx-auto max-w-6xl">
              <h2 className="text-foreground mb-12 text-center text-3xl font-bold md:text-4xl">
                {t("landing.examples.title")}
              </h2>

              <div className="grid gap-8 md:grid-cols-3">
                <Card className="bg-card/70 transform border-0 shadow-lg backdrop-blur-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
                  <CardContent className="p-6">
                    <div className="mb-4">
                      <div className="bg-muted mb-3 rounded-lg p-4">
                        <p className="text-foreground font-medium">
                          "{t("landing.examples.physics.user")}"
                        </p>
                      </div>
                      <div className="rounded-lg bg-accent p-4">
                        <p className="text-foreground">
                          "{t("landing.examples.physics.ai")}"
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-card/70 transform border-0 shadow-lg backdrop-blur-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
                  <CardContent className="p-6">
                    <div className="mb-4">
                      <div className="bg-muted mb-3 rounded-lg p-4">
                        <p className="text-foreground font-medium">
                          "{t("landing.examples.biology.user")}"
                        </p>
                      </div>
                      <div className="rounded-lg bg-accent p-4">
                        <p className="text-foreground">
                          "{t("landing.examples.biology.ai")}"
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-card/70 transform border-0 shadow-lg backdrop-blur-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
                  <CardContent className="p-6">
                    <div className="mb-4">
                      <div className="bg-muted mb-3 rounded-lg p-4">
                        <p className="text-foreground font-medium">
                          "{t("landing.examples.politics.user")}"
                        </p>
                      </div>
                      <div className="rounded-lg bg-accent p-4">
                        <p className="text-foreground">
                          "{t("landing.examples.politics.ai")}"
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* Footer Section */}
          <section className="px-6 pt-16 pb-20 text-center">
            <div className="mx-auto max-w-4xl">
              <p className="text-muted-foreground text-xl leading-relaxed font-light md:text-2xl">
                {t("landing.footer.description")}
              </p>
            </div>
          </section>
        </div>
      </div>
    );
  }

  // Show explanation interface for authenticated users
  return (
    <div className="container mx-auto px-4 pb-24 md:pb-32">
      {/* Loading Overlay */}
      <LoadingOverlay isVisible={isSubmitting} type="evaluation" />

      <div className="mx-auto max-w-2xl pt-8 md:pt-8">
        {/* Sibling Character Image */}
        <div className="mb-0 flex justify-center pt-4 md:pt-4">
          <div className="relative">
            <img
              src="/img/bro0.png"
              alt="AI 내동생 캐릭터"
              className="h-56 w-56 object-contain md:h-72 md:w-72 lg:h-92 lg:w-92"
            />

            {/* Speech Bubble - Safe positioning above image */}
            <div className="absolute -top-4 left-1/2 z-50 -translate-x-1/2 -translate-y-full md:-top-6">
              <div className="relative">
                {/* Bubble Content */}
                <div className="rounded-2xl bg-white px-3 py-1.5 shadow-lg ring-1 ring-black/5 md:px-4 md:py-2 dark:bg-gray-800 dark:ring-white/10">
                  <p className="text-xs font-medium whitespace-nowrap text-gray-900 md:text-sm dark:text-gray-100">
                    형아 안녕! 👋
                  </p>
                </div>

                {/* Bubble Tail pointing down to image */}
                <div className="absolute top-full left-1/2 -translate-x-1/2">
                  <div className="h-0 w-0 border-t-[6px] border-r-[6px] border-l-[6px] border-t-white border-r-transparent border-l-transparent md:border-t-[8px] md:border-r-[8px] md:border-l-[8px] dark:border-t-gray-800"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Usage Limit Warning for Free Users */}
        {!loaderData.isPaidUser && loaderData.usedSessions >= 2 && (
          <div className="mb-4">
            <UpgradePrompt usedSessions={loaderData.usedSessions} />
          </div>
        )}

        {/* Main Input Form */}
        <div className="bg-card mb-2 rounded-lg border p-2 md:mb-3 md:p-3">
          <h3 className="mb-1 text-center text-sm font-semibold md:mb-2 md:text-base lg:text-lg">
            동생에게 한 가지 지식을 가르쳐 주세요
          </h3>
          <Form
            action="/explain"
            method="post"
            className="space-y-1.5 md:space-y-2"
          >
            <div>
              <Label htmlFor="concept" className="text-xs">
                가르칠 내용
                <span className="ml-2 text-xs text-gray-500">
                  (개념명 또는 주제)
                </span>
              </Label>
              <Input
                id="concept"
                name="concept"
                placeholder="예: 광합성 / 온도에 따른 기체 부피 변화 / 친구를 도와야 하는 이유"
                value={concept}
                onChange={(e) => setConcept(e.target.value)}
                required
                maxLength={150}
                className="mt-0.5 h-7 text-sm md:h-8"
              />
            </div>

            <div>
              <Label htmlFor="explanation" className="text-xs">
                설명
              </Label>
              <Textarea
                id="explanation"
                name="explanation"
                placeholder="초등학생 동생이 이해할 수 있도록 쉽게 설명해 주세요..."
                value={explanation}
                onChange={(e) => setExplanation(e.target.value)}
                required
                minLength={10}
                maxLength={1000}
                rows={3}
                className="md:rows-4 mt-0.5 resize-none text-sm"
              />
              <p className="text-muted-foreground mt-0.5 text-xs">
                {explanation.length}/1000자
              </p>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting || !concept.trim() || !explanation.trim()}
              className="w-full bg-primary text-primary-foreground text-xs hover:brightness-95"
              size="sm"
            >
              {isSubmitting
                ? "AI 동생이 생각 중..."
                : "AI 동생에게 설명하기 💬"}
            </Button>
          </Form>
        </div>

        {/* Collapsible Explanation Tips */}
        <Collapsible open={isTipsOpen} onOpenChange={setIsTipsOpen}>
          <CollapsibleTrigger asChild>
            <div className="cursor-pointer rounded-md border border-yellow-200 bg-yellow-50 px-2 py-0.5 transition-colors hover:bg-yellow-100 md:py-1 dark:border-yellow-800 dark:bg-yellow-950/30 dark:hover:bg-yellow-950/50">
              <div className="flex h-4 items-center justify-between md:h-5">
                <div className="flex items-center text-yellow-800 dark:text-yellow-200">
                  <Lightbulb className="mr-1 h-3 w-3" />
                  <span className="text-xs">효과적인 설명 팁</span>
                </div>
                <div className="text-yellow-600 dark:text-yellow-400">
                  {isTipsOpen ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                </div>
              </div>
            </div>
          </CollapsibleTrigger>

          <CollapsibleContent>
            <Card className="mt-1 border-yellow-200 bg-yellow-50 md:mt-2 dark:border-yellow-800 dark:bg-yellow-950/30">
              <CardContent className="p-3 pt-0 md:p-4">
                <div className="grid grid-cols-1 gap-3 pt-3 md:grid-cols-2 md:gap-4 md:pt-4">
                  <div className="space-y-2">
                    <h4 className="font-semibold text-yellow-900 dark:text-yellow-100">
                      🎯 좋은 설명
                    </h4>
                    <ul className="space-y-1 text-xs text-yellow-800 md:text-sm dark:text-yellow-200">
                      <li>• 일상생활 예시 사용</li>
                      <li>• 단계별로 차근차근 설명</li>
                      <li>• 쉬운 단어 사용</li>
                      <li>• 비유와 은유 활용</li>
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-semibold text-yellow-900 dark:text-yellow-100">
                      ❌ 피해야 할 것
                    </h4>
                    <ul className="space-y-1 text-xs text-yellow-800 md:text-sm dark:text-yellow-200">
                      <li>• 전문용어 남발</li>
                      <li>• 너무 복잡한 설명</li>
                      <li>• 추상적인 개념만 나열</li>
                      <li>• 예시 없는 설명</li>
                    </ul>
                  </div>
                </div>

                <div className="mt-3 rounded bg-yellow-100 p-2 md:mt-4 md:p-3 dark:bg-yellow-900/50">
                  <p className="text-xs text-yellow-700 dark:text-yellow-300">
                    💭 <strong>활용 팁:</strong> 이 방법들을 참고해서 동생에게
                    설명해보세요! 더 궁금한 점이 있으면 동생에게 계속
                    질문해보세요.
                  </p>
                </div>
              </CardContent>
            </Card>
          </CollapsibleContent>
        </Collapsible>
      </div>

      {/* 가이드 영상 모달 */}
      <GuideVideoModal
        isOpen={showGuideVideo}
        onClose={handleCloseGuideVideo}
        onDontShowToday={handleDontShowToday}
        videoUrl="/videos/guide-video.mp4" // 실제 가이드 영상 URL로 변경 필요
        title="AI동생이 인사드려요"
      />
    </div>
  );
}
